import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { RefreshCw, Building2, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { adminOfficesApi, errMsg } from './adminOfficesApi';
import { PracticeOfficesDetail } from './PracticeOfficesDetail';

const limitLabel = (l) => (l == null ? 'Unlimited' : l);

export const OfficesAdmin = ({ adminToken }) => {
  const api = useMemo(() => adminOfficesApi(adminToken), [adminToken]);
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const reload = useCallback(async () => {
    try { setData(await api.practices()); } catch (e) { toast.error(errMsg(e)); }
  }, [api]);
  useEffect(() => { reload(); }, [reload]);

  const rows = (data?.practices || []).filter((p) => !q || `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-6" data-testid="offices-admin">
      <Toaster richColors position="top-right" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><Building2 className="w-6 h-6 text-emerald-600" />Offices & Plans</h2>
          <p className="text-sm text-slate-500">Every practice's offices, plan, office limit, staff office access and per-office numbers. Changes here never charge the practice.</p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} data-testid="offices-admin-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-3" data-testid="offices-admin-totals">
          {[['Practices', data.totals.practices], ['Open offices', data.totals.offices], ['Multi-office practices', data.totals.multiOffice]].map(([l, v]) => (
            <div key={l} className="bg-white border rounded-xl p-4"><p className="text-xs text-slate-500">{l}</p><p className="text-2xl font-bold">{v}</p></div>
          ))}
        </div>
      )}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input className="pl-9" placeholder="Search practices" value={q} onChange={(e) => setQ(e.target.value)} data-testid="offices-admin-search" />
      </div>
      <div className="bg-white border rounded-xl divide-y" data-testid="offices-admin-list">
        {!data ? <p className="p-6 text-slate-500">Loading…</p> : rows.slice(0, 200).map((p) => (
          <div key={p.id}>
            <button className="w-full flex flex-wrap items-center justify-between gap-3 p-4 text-left hover:bg-slate-50" onClick={() => setOpen(open === p.id ? null : p.id)} data-testid={`offices-admin-row-${p.id}`}>
              <div><p className="font-semibold">{p.name}</p><p className="text-xs text-slate-500">{p.email}</p></div>
              <div className="flex gap-4 text-sm text-slate-600">
                <span className="capitalize">{p.tier}</span>
                <span className={p.limit != null && p.offices > p.limit ? 'text-rose-600 font-semibold' : ''}>{p.offices} / {limitLabel(p.limit)} offices</span>
                <span>{p.patients.toLocaleString()} patients</span>
              </div>
            </button>
            {open === p.id && <PracticeOfficesDetail pid={p.id} api={api} onChanged={reload} />}
          </div>
        ))}
      </div>
    </div>
  );
};
